import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ClipboardCheck, XCircle } from "lucide-react";
import { ApiError } from "../api/client";
import { listActivities, reviewActivity, validateActivity } from "../api/activities";
import { useAuth } from "../hooks/useAuth";
import { isValidationQueueVisible } from "../lib/activity";
import { formatDate } from "../lib/format";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  Spinner,
  StatusBadge,
  Textarea,
  cn,
} from "../components/ui";

type QueueStatus = "SUBMITTED" | "UNDER_REVIEW";

/** Antrean validasi untuk admin prodi/fakultas/institut. */
export function ValidationQueuePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") ?? "SUBMITTED") as QueueStatus;

  const [rejectId, setRejectId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isAdmin = isValidationQueueVisible(user?.role);

  const queueQuery = useQuery({
    queryKey: ["activities", "queue", status],
    queryFn: () => listActivities({ status, perPage: 50, sort: "activityDate", order: "asc" }),
    enabled: isAdmin,
  });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ["activities"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  }

  const reviewMutation = useMutation({
    mutationFn: (id: string) => reviewActivity(id),
    onSuccess: () => {
      setError(null);
      setNotice("Kegiatan dipindahkan ke tahap ditelaah.");
      refresh();
    },
    onError: (caught) => {
      setNotice(null);
      setError(caught instanceof ApiError ? caught.message : "Tindakan gagal.");
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => validateActivity(id, "APPROVED"),
    onSuccess: () => {
      setError(null);
      setNotice("Kegiatan disetujui.");
      refresh();
    },
    onError: (caught) => {
      setNotice(null);
      setError(caught instanceof ApiError ? caught.message : "Tindakan gagal.");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => validateActivity(id, "REJECTED", notes),
    onSuccess: () => {
      setRejectId(null);
      setNotes("");
      setError(null);
      setNotice("Kegiatan ditolak dengan catatan.");
      refresh();
    },
    onError: (caught) => {
      setNotice(null);
      setError(caught instanceof ApiError ? caught.message : "Tindakan gagal.");
    },
  });

  if (!isAdmin) {
    return (
      <Alert tone="info">
        Antrean validasi hanya tersedia untuk admin program studi, fakultas, dan institut.
      </Alert>
    );
  }

  const items = queueQuery.data?.items ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Antrean validasi"
        description="Kegiatan pada lingkup wewenang Anda yang menunggu telaah atau keputusan."
      />

      <div className="flex gap-2">
        {(
          [
            ["SUBMITTED", "Menunggu ditelaah"],
            ["UNDER_REVIEW", "Sedang ditelaah"],
          ] as Array<[QueueStatus, string]>
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() =>
              setParams(value === "SUBMITTED" ? {} : { status: value }, { replace: true })
            }
            className={cn(
              "rounded-xl px-3 py-2 text-sm font-semibold transition",
              status === value
                ? "bg-brand-600 text-white"
                : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      {queueQuery.isLoading ? <Spinner label="Memuat antrean..." /> : null}

      {!queueQuery.isLoading && items.length === 0 ? (
        <EmptyState
          title="Antrean kosong"
          description="Tidak ada kegiatan yang menunggu tindakan pada tahap ini."
        />
      ) : null}

      <ul className="space-y-3">
        {items.map((activity) => (
          <li key={activity.id}>
            <Card className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-slate-500">{activity.activityCode}</span>
                <StatusBadge status={activity.status} />
              </div>

              <div>
                <Link
                  to={`/kegiatan/${activity.id}`}
                  className="text-base font-semibold text-slate-900 hover:underline"
                >
                  {activity.title}
                </Link>
                <p className="mt-1 text-xs text-slate-500">
                  {formatDate(activity.activityDate)} · {activity.category.name} ·{" "}
                  {activity.studyProgram.name} · {activity.submittedBy.fullName}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {activity.status === "SUBMITTED" ? (
                  <Button
                    variant="secondary"
                    loading={reviewMutation.isPending}
                    onClick={() => reviewMutation.mutate(activity.id)}
                  >
                    <ClipboardCheck className="size-4" aria-hidden />
                    Mulai telaah
                  </Button>
                ) : null}
                <Button
                  loading={approveMutation.isPending}
                  onClick={() => approveMutation.mutate(activity.id)}
                >
                  <CheckCircle2 className="size-4" aria-hidden />
                  Setujui
                </Button>
                <Button variant="danger" onClick={() => setRejectId(activity.id)}>
                  <XCircle className="size-4" aria-hidden />
                  Tolak
                </Button>
              </div>
            </Card>
          </li>
        ))}
      </ul>

      <Modal
        open={rejectId !== null}
        title="Tolak kegiatan"
        onClose={() => setRejectId(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejectId(null)}>
              Batal
            </Button>
            <Button
              variant="danger"
              loading={rejectMutation.isPending}
              onClick={() => {
                if (rejectId) rejectMutation.mutate(rejectId);
              }}
            >
              Tolak kegiatan
            </Button>
          </>
        }
      >
        <Field label="Alasan penolakan" hint="Minimal 5 karakter." htmlFor="queueNotes">
          <Textarea
            id="queueNotes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}
